import type { ReactElement, ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

export type AdminSection =
  | 'dashboard'
  | 'queue'
  | 'reference-takes'
  | 'pieces'
  | 'arrangements'
  | 'tunebooks'
  | 'users'
  | 'audit';

export type DAdminShellProps = {
  section: AdminSection;
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  children?: ReactNode;
};

const INSTANCE_FALLBACK = 'local';
const COMING_SOON_HINT = 'Coming soon';

function instanceName(): string {
  const value = import.meta.env.VITE_INSTANCE_NAME as string | undefined;
  return value && value.trim().length > 0 ? value : INSTANCE_FALLBACK;
}

type ActiveTab = {
  section: AdminSection;
  label: string;
  to: '/admin' | '/admin/queue' | '/admin/reference-takes';
};
type DisabledTab = { label: string };

const ACTIVE_TABS: readonly ActiveTab[] = [
  { section: 'dashboard', label: 'Overview', to: '/admin' },
  { section: 'queue', label: 'Moderation queue', to: '/admin/queue' },
  { section: 'reference-takes', label: 'Reference takes', to: '/admin/reference-takes' },
];

const DISABLED_TABS: readonly DisabledTab[] = [
  { label: 'Pieces' },
  { label: 'Arrangements' },
  { label: 'Tunebooks' },
  { label: 'Users' },
  { label: 'Audit log' },
];

const TAB_CLASS = 'h-8 px-3 rounded-[var(--r-sm)] text-[12px] font-medium transition-colors';

export function DAdminShell({
  section, title, subtitle, actions, children,
}: DAdminShellProps): ReactElement {
  return (
    <div className="flex h-full flex-col bg-[var(--bg-page)]">
      <div className="h-12 px-8 flex items-center gap-4 border-b border-[var(--rule)] bg-[var(--ink)] text-white">
        <span className="text-[11px] font-mono tracking-[0.2em] uppercase">
          Admin · root@{instanceName()}
        </span>
      </div>
      <div className="h-12 px-8 flex items-center gap-1 border-b border-[var(--rule)] bg-[var(--bg-surface)]">
        {ACTIVE_TABS.map((tab) => (
          <Link
            key={tab.section}
            to={tab.to}
            aria-current={section === tab.section ? 'page' : undefined}
            className={`${TAB_CLASS} text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-soft)] aria-[current=page]:bg-[var(--bg-soft)] aria-[current=page]:text-[var(--ink)]`}
          >
            {tab.label}
          </Link>
        ))}
        {DISABLED_TABS.map((tab) => (
          <button
            key={tab.label}
            type="button"
            aria-disabled="true"
            title={COMING_SOON_HINT}
            className={`${TAB_CLASS} text-[var(--ink-4)] cursor-not-allowed`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {title && (
        <div className="px-8 py-5 border-b border-[var(--rule)] flex items-end justify-between bg-[var(--bg-surface)]">
          <div>
            <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-[var(--ink-3)]">
              {section}
            </div>
            <h1 className="font-display text-[26px] font-semibold mt-1 leading-tight">{title}</h1>
            {subtitle && (
              <div className="text-[12px] text-[var(--ink-3)] mt-0.5">{subtitle}</div>
            )}
          </div>
          {actions && <div className="flex items-center gap-2">{actions}</div>}
        </div>
      )}
      <div className="flex-1 overflow-auto bg-[var(--bg-page)]">{children}</div>
    </div>
  );
}
